/**
 * Turn a uniform flat background into transparency by sampling corners (typical AI "icon on white").
 * Does not use CORS — works on same-origin or blob: URLs from canvas drawImage.
 */
export function applyTransparentBackgroundFromBlob(blob, options = {}) {
  const {
    tolerance = 38,
    feather = 12,
  } = options;

  return new Promise((resolve) => {
    if (!blob || !blob.type?.startsWith('image/')) {
      resolve(blob);
      return;
    }

    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) {
          URL.revokeObjectURL(url);
          resolve(blob);
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data;

        const idx = (x, y) => (Math.min(Math.max(x, 0), w - 1) + Math.min(Math.max(y, 0), h - 1) * w) * 4;

        const corners = [
          [data[idx(0, 0)], data[idx(0, 0) + 1], data[idx(0, 0) + 2]],
          [data[idx(w - 1, 0)], data[idx(w - 1, 0) + 1], data[idx(w - 1, 0) + 2]],
          [data[idx(0, h - 1)], data[idx(0, h - 1) + 1], data[idx(0, h - 1) + 2]],
          [data[idx(w - 1, h - 1)], data[idx(w - 1, h - 1) + 1], data[idx(w - 1, h - 1) + 2]],
        ];

        let maxCornerSpread = 0;
        for (let a = 0; a < 4; a++) {
          for (let b = a + 1; b < 4; b++) {
            const d = Math.hypot(
              corners[a][0] - corners[b][0],
              corners[a][1] - corners[b][1],
              corners[a][2] - corners[b][2]
            );
            maxCornerSpread = Math.max(maxCornerSpread, d);
          }
        }

        // If corners disagree a lot, assume white studio background (common from generators)
        let br = 0;
        let bg = 0;
        let bb = 0;
        if (maxCornerSpread > 55) {
          br = 255;
          bg = 255;
          bb = 255;
        } else {
          for (const c of corners) {
            br += c[0];
            bg += c[1];
            bb += c[2];
          }
          br /= 4;
          bg /= 4;
          bb /= 4;
        }

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const dist = Math.hypot(r - br, g - bg, b - bb);
          if (dist <= tolerance) {
            data[i + 3] = 0;
          } else if (feather > 0 && dist <= tolerance + feather) {
            const t = (dist - tolerance) / feather;
            data[i + 3] = Math.round(data[i + 3] * Math.min(1, t));
          }
        }

        ctx.putImageData(imageData, 0, 0);
        URL.revokeObjectURL(url);

        canvas.toBlob(
          (out) => resolve(out || blob),
          'image/png',
          1
        );
      } catch {
        URL.revokeObjectURL(url);
        resolve(blob);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(blob);
    };
    img.src = url;
  });
}
